// contracts/transcript-document.ts
//
// Pure shared transcript document contract, imported by UI and backend.
// No AWS, DOM, Vue, or Node-only imports: parsing is deterministic and bounded.
//
// Supported canonical inputs:
// - Provider JSON: { output: { segments: [...] } } or { segments: [...] }
//   (explicit empty segments arrays are valid timed documents with zero cues)
// - Draft JSON envelopes: legacy { segments, settings } and new
//   { kind: 'timed', segments } / { kind: 'text', text }
// - SRT: numbered cue blocks, HH:MM:SS,mmm (comma) or HH:MM:SS.mmm (dot), CRLF
// - WebVTT: WEBVTT header, optional cue IDs, hh:mm:ss.mmm or mm:ss.mmm,
//   cue settings, NOTE/STYLE/REGION blocks ignored
// - Plain text / OCR JSON text-only: untimed documents (never fake cues)
//
// Timed cues preserve finite start/end, speaker, per-word data, nonempty text,
// and stable deterministic IDs. Unsupported/malformed JSON reports a meaningful
// error instead of silently becoming an empty transcript. Nonempty malformed
// subtitles error instead of silently dropping content.

export type TranscriptContentFormat = 'json' | 'srt' | 'vtt' | 'txt' | 'unknown';
export type TranscriptContentKind = 'timed' | 'text';
export type TranscriptProvenance = 'provider' | 'import' | 'recovered_version' | 'legacy';

export interface TranscriptWord {
  word: string;
  start: number;
  end: number;
  speaker?: string;
}

export interface TranscriptCue {
  id: string;
  start: number;
  end: number;
  text: string;
  speaker?: string;
  words?: TranscriptWord[];
}

export interface TimedTranscriptDocument {
  kind: 'timed';
  format: 'json' | 'srt' | 'vtt';
  cues: TranscriptCue[];
  language?: string;
  fileName?: string;
  settings?: Record<string, unknown>;
}

export interface TextTranscriptDocument {
  kind: 'text';
  format: 'txt' | 'json-text';
  text: string;
  language?: string;
  fileName?: string;
  settings?: Record<string, unknown>;
}

export type TranscriptDocument = TimedTranscriptDocument | TextTranscriptDocument;

export class TranscriptDocumentError extends Error {
  readonly code:
    | 'unsupported-json'
    | 'malformed-srt'
    | 'malformed-vtt'
    | 'empty-content'
    | 'too-large'
    | 'too-many-cues';
  constructor(code: TranscriptDocumentError['code'], message: string) {
    super(message);
    this.name = 'TranscriptDocumentError';
    this.code = code;
  }
}

export const TRANSCRIPT_DOCUMENT_LIMITS = {
  maxBytes: 50 * 1024 * 1024,
  maxCues: 250000,
} as const;

function byteLength(value: string): number {
  // TextEncoder exists in Node 11+ and all browsers; avoid Buffer for UI use.
  try {
    return new TextEncoder().encode(value).length;
  } catch {
    return value.length;
  }
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function normalizeId(raw: unknown, fallback: string): string {
  if (typeof raw === 'string' && raw.length > 0) return raw;
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  return fallback;
}

// Deterministic duplicate-ID handling: first occurrence keeps its ID,
// later duplicates gain "-2", "-3", ... suffixes in document order.
// All original IDs are reserved up front so a generated suffix never
// collides with an ID that already exists in the document (e.g.
// ['a','a','a-2'] -> ['a','a-3','a-2']).
export function dedupeCueIds(cues: TranscriptCue[]): TranscriptCue[] {
  const reserved = new Set<string>();
  for (const cue of cues) reserved.add(cue.id || '1');
  const used = new Set<string>();
  return cues.map(cue => {
    const base = cue.id || '1';
    if (!used.has(base)) {
      used.add(base);
      return cue;
    }
    let n = 2;
    let candidate = `${base}-${n}`;
    while (used.has(candidate) || reserved.has(candidate)) {
      n++;
      candidate = `${base}-${n}`;
    }
    used.add(candidate);
    return { ...cue, id: candidate };
  });
}

function normalizeWord(raw: any, index: number): TranscriptWord {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new TranscriptDocumentError('unsupported-json', `Invalid word entry at index ${index}: expected an object`);
  }
  const candidate = typeof raw.word === 'string' ? raw.word : typeof raw.text === 'string' ? raw.text : null;
  if (candidate === null || candidate.length === 0) {
    throw new TranscriptDocumentError('unsupported-json', `Invalid word entry at index ${index}: missing word/text`);
  }
  if (typeof raw.start === 'boolean' || (typeof raw.start === 'object' && raw.start !== null) || isMissingTime(raw.start)) {
    throw new TranscriptDocumentError('unsupported-json', `Invalid word timings at index ${index}: start must be a finite nonnegative number`);
  }
  if (typeof raw.end === 'boolean' || (typeof raw.end === 'object' && raw.end !== null) || isMissingTime(raw.end)) {
    throw new TranscriptDocumentError('unsupported-json', `Invalid word timings at index ${index}: end must be a finite nonnegative number`);
  }
  const start = Number(raw.start);
  const end = Number(raw.end);
  if (!Number.isFinite(start) || start < 0) {
    throw new TranscriptDocumentError('unsupported-json', `Invalid word timings at index ${index}: start must be a finite nonnegative number`);
  }
  if (!Number.isFinite(end) || end < 0) {
    throw new TranscriptDocumentError('unsupported-json', `Invalid word timings at index ${index}: end must be a finite nonnegative number`);
  }
  if (end < start) {
    throw new TranscriptDocumentError('unsupported-json', `Invalid word timings at index ${index}: end precedes start`);
  }
  const out: TranscriptWord = { word: candidate, start, end };
  if (typeof raw.speaker === 'string' && raw.speaker.length > 0) out.speaker = raw.speaker;
  return out;
}

function isMissingTime(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
}

function toFiniteNonnegativeTime(value: unknown, what: string): number {
  if (typeof value === 'boolean' || (typeof value === 'object' && value !== null)) {
    throw new TranscriptDocumentError('unsupported-json', `Invalid ${what}: expected a finite nonnegative number`);
  }
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) {
    throw new TranscriptDocumentError('unsupported-json', `Invalid ${what}: expected a finite nonnegative number`);
  }
  return num;
}

function normalizeTimedSegment(raw: any, index: number): TranscriptCue {
  const fallbackId = String(index + 1);
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new TranscriptDocumentError('unsupported-json', `Invalid segment at index ${index}: expected an object`);
  }
  let words: TranscriptWord[] | undefined;
  if (raw.words !== undefined && raw.words !== null) {
    if (!Array.isArray(raw.words)) {
      throw new TranscriptDocumentError('unsupported-json', `Invalid segment ${fallbackId}: words must be an array`);
    }
    const parsed = raw.words.map((w: any, wi: number) => normalizeWord(w, wi));
    if (parsed.length > 0) words = parsed;
  }
  let text: string;
  if (typeof raw.text === 'string') {
    text = raw.text;
  } else if (raw.text === undefined || raw.text === null) {
    if (words && words.length > 0) {
      text = words.map(w => w.word).join(' ');
    } else {
      throw new TranscriptDocumentError('unsupported-json', `Invalid segment ${fallbackId}: missing text`);
    }
  } else {
    throw new TranscriptDocumentError('unsupported-json', `Invalid segment ${fallbackId}: text must be a string`);
  }
  const hasStart = !isMissingTime(raw.start);
  const hasEnd = !isMissingTime(raw.end);
  let start: number | undefined;
  let end: number | undefined;
  if (hasStart) start = toFiniteNonnegativeTime(raw.start, `segment ${fallbackId} start`);
  if (hasEnd) end = toFiniteNonnegativeTime(raw.end, `segment ${fallbackId} end`);
  if (start === undefined || end === undefined) {
    if (!words || words.length === 0) {
      throw new TranscriptDocumentError('unsupported-json', `Invalid segment ${fallbackId}: missing timings with no word bounds to derive from`);
    }
    let minStart = words[0].start;
    let maxEnd = words[0].end;
    for (const w of words) {
      if (w.start < minStart) minStart = w.start;
      if (w.end > maxEnd) maxEnd = w.end;
    }
    if (start === undefined) start = minStart;
    if (end === undefined) end = maxEnd;
  }
  if (end < start) {
    throw new TranscriptDocumentError('unsupported-json', `Invalid segment ${fallbackId}: end precedes start`);
  }
  const cue: TranscriptCue = { id: normalizeId(raw.id, fallbackId), start, end, text };
  if (typeof raw.speaker === 'string' && raw.speaker.length > 0) cue.speaker = raw.speaker;
  if (words && words.length > 0) cue.words = words;
  return cue;
}

function extractSettings(value: unknown): Record<string, unknown> | undefined {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const settings = (value as Record<string, unknown>).settings;
    if (settings && typeof settings === 'object' && !Array.isArray(settings)) {
      return settings as Record<string, unknown>;
    }
  }
  return undefined;
}

function resolveMeta(opts: { fileName?: string; language?: string }, obj?: Record<string, any>): { fileName?: string; language?: string; settings?: Record<string, unknown> } {
  let language = opts.language;
  let fileName = opts.fileName;
  let settings: Record<string, unknown> | undefined;
  if (obj && typeof obj === 'object') {
    if (language === undefined && typeof obj.language === 'string') language = obj.language;
    if (fileName === undefined && typeof obj.fileName === 'string') fileName = obj.fileName;
    settings = extractSettings(obj);
  }
  return { language, fileName, settings };
}

function withMeta<T extends { language?: string; fileName?: string; settings?: Record<string, unknown> }>(
  doc: T, meta: { language?: string; fileName?: string; settings?: Record<string, unknown> }
): T {
  if (meta.language !== undefined) (doc as any).language = meta.language;
  if (meta.fileName !== undefined) (doc as any).fileName = meta.fileName;
  if (meta.settings !== undefined) (doc as any).settings = meta.settings;
  return doc;
}

function checkObjectInputSize(value: unknown): void {
  let serialized: string;
  try {
    serialized = JSON.stringify(value);
  } catch {
    throw new TranscriptDocumentError('unsupported-json', 'Unsupported transcript JSON: value is not serializable (cyclic structure)');
  }
  if (serialized === undefined) {
    throw new TranscriptDocumentError('unsupported-json', 'Unsupported transcript JSON: expected an object with segments or text');
  }
  if (byteLength(serialized) > TRANSCRIPT_DOCUMENT_LIMITS.maxBytes) {
    throw new TranscriptDocumentError('too-large', 'Transcript content exceeds the supported size');
  }
}

function parseSegmentsArray(segments: unknown[], opts: { fileName?: string; language?: string }, settings?: Record<string, unknown>): TimedTranscriptDocument {
  if (segments.length > TRANSCRIPT_DOCUMENT_LIMITS.maxCues) {
    throw new TranscriptDocumentError('too-many-cues', 'Transcript exceeds the supported cue count');
  }
  const cues = dedupeCueIds(segments.map((s: any, i: number) => normalizeTimedSegment(s, i)));
  const doc: TimedTranscriptDocument = { kind: 'timed', format: 'json', cues };
  return withMeta(doc, { language: opts.language, fileName: opts.fileName, settings });
}

function parseJsonValue(value: unknown, opts: { fileName?: string; language?: string }): TranscriptDocument {
  if (Array.isArray(value)) {
    return parseSegmentsArray(value, opts, undefined);
  }
  if (!value || typeof value !== 'object') {
    throw new TranscriptDocumentError('unsupported-json', 'Unsupported transcript JSON: expected an object with segments or text');
  }
  const obj = value as Record<string, any>;
  const meta = resolveMeta(opts, obj);
  // New explicit envelopes first.
  if (obj.kind === 'timed') {
    if (!Array.isArray(obj.segments)) {
      throw new TranscriptDocumentError('unsupported-json', 'Timed transcript JSON requires a segments array');
    }
    return parseSegmentsArray(obj.segments, { language: meta.language, fileName: meta.fileName }, meta.settings);
  }
  if (obj.kind === 'text') {
    if (typeof obj.text !== 'string') {
      throw new TranscriptDocumentError('unsupported-json', 'Text transcript JSON requires text');
    }
    const doc: TextTranscriptDocument = { kind: 'text', format: 'json-text', text: obj.text };
    return withMeta(doc, meta);
  }
  const segments = (obj as any)?.output?.segments ?? (obj as any)?.segments ?? null;
  if (Array.isArray(segments)) {
    // Explicit empty segments arrays are valid timed documents with zero cues.
    return parseSegmentsArray(segments, { language: meta.language, fileName: meta.fileName }, meta.settings);
  }
  // OCR / text-only JSON: plain text carriers are untimed, never fake cues.
  const textCandidate =
    (typeof obj.text === 'string' ? obj.text : null) ??
    (typeof obj.transcript === 'string' ? obj.transcript : null) ??
    (typeof obj.content === 'string' ? obj.content : null) ??
    (obj.output && typeof obj.output.text === 'string' ? obj.output.text : null) ??
    (obj.output && typeof obj.output.transcript === 'string' ? obj.output.transcript : null);
  if (typeof textCandidate === 'string') {
    if (!textCandidate.trim()) {
      throw new TranscriptDocumentError('unsupported-json', 'Text transcript JSON contains only empty text');
    }
    const doc: TextTranscriptDocument = { kind: 'text', format: 'json-text', text: textCandidate };
    return withMeta(doc, meta);
  }
  throw new TranscriptDocumentError(
    'unsupported-json',
    'Unsupported transcript JSON: expected output.segments, segments, or text content'
  );
}

function parseSrtTimestamp(value: string): number {
  const match = /^(\d{1,2}):(\d{2}):(\d{2})[,.](\d{3})$/.exec(value.trim());
  if (!match) throw new TranscriptDocumentError('malformed-srt', `Invalid SRT timestamp: ${value}`);
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  const millis = Number(match[4]);
  if (minutes >= 60 || seconds >= 60) {
    throw new TranscriptDocumentError('malformed-srt', `Invalid SRT timestamp: ${value}`);
  }
  return hours * 3600 + minutes * 60 + seconds + millis / 1000;
}

function parseVttTimestamp(value: string): number {
  const trimmed = value.trim();
  let match = /^(\d{2,}):(\d{2}):(\d{2})\.(\d{3})$/.exec(trimmed);
  if (match) {
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3]);
    const millis = Number(match[4]);
    if (minutes >= 60 || seconds >= 60) {
      throw new TranscriptDocumentError('malformed-vtt', `Invalid WebVTT timestamp: ${value}`);
    }
    return hours * 3600 + minutes * 60 + seconds + millis / 1000;
  }
  match = /^(\d{2}):(\d{2})\.(\d{3})$/.exec(trimmed);
  if (match) {
    const minutes = Number(match[1]);
    const seconds = Number(match[2]);
    const millis = Number(match[3]);
    if (minutes >= 60 || seconds >= 60) {
      throw new TranscriptDocumentError('malformed-vtt', `Invalid WebVTT timestamp: ${value}`);
    }
    return minutes * 60 + seconds + millis / 1000;
  }
  throw new TranscriptDocumentError('malformed-vtt', `Invalid WebVTT timestamp: ${value}`);
}

function parseSrtDocument(text: string, opts: { fileName?: string; language?: string }): TimedTranscriptDocument {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (!normalized.trim()) {
    throw new TranscriptDocumentError('empty-content', 'Subtitle content is empty');
  }
  // Split on blank lines; every nonempty block must be a valid numbered cue.
  // Nothing is silently discarded: any unparsed block is malformed subtitles.
  const blocks = normalized.split(/\n\s*\n/).map(b => b.replace(/^\n+|\n+$/g, '')).filter(b => b.length > 0);
  const cues: TranscriptCue[] = [];
  for (const block of blocks) {
    const lines = block.split('\n');
    if (lines.length < 2) {
      throw new TranscriptDocumentError('malformed-srt', `Malformed SRT cue block: ${lines[0] ?? ''}`);
    }
    if (!/^\d+$/.test(lines[0].trim())) {
      throw new TranscriptDocumentError('malformed-srt', `Malformed SRT cue block: expected numeric cue id, got: ${lines[0].trim()}`);
    }
    const timing = lines[1].trim();
    const arrow = timing.split(/-->/);
    if (arrow.length !== 2) {
      throw new TranscriptDocumentError('malformed-srt', `Malformed SRT cue timestamps: ${timing}`);
    }
    const start = parseSrtTimestamp(arrow[0]);
    const end = parseSrtTimestamp(arrow[1]);
    if (!(end > start) || !Number.isFinite(start) || !Number.isFinite(end)) {
      throw new TranscriptDocumentError('malformed-srt', `Invalid SRT cue timings: ${timing}`);
    }
    // Markup (<i>, <b>, ...) is preserved as literal text; the UI escapes it.
    const cueText = lines.slice(2).join('\n').trim();
    if (!cueText) {
      throw new TranscriptDocumentError('malformed-srt', 'SRT cue is missing text');
    }
    cues.push({ id: lines[0].trim(), start, end, text: cueText });
  }
  if (cues.length === 0) {
    throw new TranscriptDocumentError('malformed-srt', 'Malformed SRT content: expected numbered cue blocks with timestamps');
  }
  if (cues.length > TRANSCRIPT_DOCUMENT_LIMITS.maxCues) {
    throw new TranscriptDocumentError('too-many-cues', 'Transcript exceeds the supported cue count');
  }
  cues.sort((a, b) => a.start - b.start);
  return { kind: 'timed', format: 'srt', cues: dedupeCueIds(cues), language: opts.language, fileName: opts.fileName };
}

function parseVttDocument(text: string, opts: { fileName?: string; language?: string }): TimedTranscriptDocument {
  const withoutBom = text.replace(/^\uFEFF/, '');
  const normalized = withoutBom.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split('\n');
  if (lines.length === 0 || lines[0].replace(/^\uFEFF/, '').trim().split(/\s+/)[0] !== 'WEBVTT') {
    throw new TranscriptDocumentError('malformed-vtt', 'WebVTT content must start with a WEBVTT header');
  }
  const cues: TranscriptCue[] = [];
  let i = 1;
  let autoId = 0;
  const skipMetadataBlock = (header: string): boolean =>
    header === 'NOTE' || header === 'STYLE' || header === 'REGION';
  while (i < lines.length) {
    // Skip blank lines.
    if (!lines[i].trim()) { i++; continue; }
    const header = lines[i].trim().split(/\s+/)[0];
    if (skipMetadataBlock(header)) {
      // NOTE/STYLE/REGION blocks run until the next blank line.
      i++;
      while (i < lines.length && lines[i].trim()) i++;
      continue;
    }
    let cueId: string | undefined;
    let timingLine = lines[i].trim();
    // Optional cue identifier: a line without "-->" followed by a timing line.
    if (!timingLine.includes('-->')) {
      cueId = timingLine;
      i++;
      if (i >= lines.length) {
        throw new TranscriptDocumentError('malformed-vtt', `Malformed WebVTT cue: identifier without timing: ${cueId}`);
      }
      timingLine = lines[i].trim();
      if (!timingLine) {
        throw new TranscriptDocumentError('malformed-vtt', `Malformed WebVTT cue: identifier without timing: ${cueId}`);
      }
      const nextHeader = timingLine.split(/\s+/)[0];
      if (skipMetadataBlock(nextHeader)) {
        throw new TranscriptDocumentError('malformed-vtt', `Malformed WebVTT cue: identifier without timing: ${cueId}`);
      }
    }
    if (!timingLine.includes('-->')) {
      throw new TranscriptDocumentError('malformed-vtt', `Malformed WebVTT cue timing: ${timingLine}`);
    }
    const [left, ...rest] = timingLine.split('-->');
    const rightAndSettings = rest.join('-->').trim();
    if (!left || !rightAndSettings) {
      throw new TranscriptDocumentError('malformed-vtt', `Malformed WebVTT cue timing: ${timingLine}`);
    }
    const rightToken = rightAndSettings.split(/\s+/)[0];
    const start = parseVttTimestamp(left);
    const end = parseVttTimestamp(rightToken);
    if (!(end > start) || !Number.isFinite(start) || !Number.isFinite(end)) {
      throw new TranscriptDocumentError('malformed-vtt', `Invalid WebVTT cue timings: ${timingLine}`);
    }
    i++;
    const textLines: string[] = [];
    while (i < lines.length && lines[i].trim()) {
      textLines.push(lines[i]);
      i++;
    }
    // Markup is preserved as literal text; the UI escapes it.
    const cueText = textLines.join('\n').trim();
    if (!cueText) {
      throw new TranscriptDocumentError('malformed-vtt', 'WebVTT cue is missing text');
    }
    autoId++;
    cues.push({ id: cueId || String(autoId), start, end, text: cueText });
  }
  if (cues.length === 0) {
    throw new TranscriptDocumentError('malformed-vtt', 'WebVTT content contains no timed cues');
  }
  if (cues.length > TRANSCRIPT_DOCUMENT_LIMITS.maxCues) {
    throw new TranscriptDocumentError('too-many-cues', 'Transcript exceeds the supported cue count');
  }
  cues.sort((a, b) => a.start - b.start);
  return { kind: 'timed', format: 'vtt', cues: dedupeCueIds(cues), language: opts.language, fileName: opts.fileName };
}

function isJsonDeclared(fileName?: string, contentType?: string): boolean {
  const lowerName = String(fileName || '').toLowerCase();
  const lowerType = String(contentType || '').toLowerCase();
  return lowerName.endsWith('.json') || lowerType.includes('json');
}

function isTextDeclared(fileName?: string, contentType?: string): boolean {
  const lowerName = String(fileName || '').toLowerCase();
  const lowerType = String(contentType || '').toLowerCase();
  return lowerName.endsWith('.txt') || lowerType.startsWith('text/plain');
}

export function detectTranscriptFormat(raw: string, fileName?: string): TranscriptContentFormat {
  const trimmed = raw.replace(/^\uFEFF/, '').trim();
  if (!trimmed) return 'unknown';
  const lowerName = String(fileName || '').toLowerCase();
  if (lowerName.endsWith('.txt')) return 'txt';
  if (lowerName.endsWith('.json')) return 'json';
  if (/^\s*[\[{]/.test(trimmed)) return 'json';
  if (/^WEBVTT(\s|$)/.test(trimmed)) return 'vtt';
  if (/-->/.test(trimmed)) {
    // VTT without header is still treated as VTT-like for error reporting;
    // numbered SRT blocks are SRT.
    if (/(^|\n)\d+\n\d{1,2}:\d{2}:\d{2}[,.]\d{3}\s*-->/.test(trimmed)) return 'srt';
    if (lowerName.endsWith('.vtt')) return 'vtt';
    return 'srt';
  }
  if (lowerName.endsWith('.srt')) return 'srt';
  if (lowerName.endsWith('.vtt')) return 'vtt';
  return 'txt';
}

// Main entry point. Accepts raw text (or an already-parsed JSON object) plus
// an optional file-name/content hint. Returns a timed or text document.
// Throws TranscriptDocumentError with a meaningful message for unsupported or
// malformed input instead of silently producing an empty transcript.
export function parseTranscriptDocument(
  input: string | unknown,
  opts: { fileName?: string; language?: string; contentType?: string } = {}
): TranscriptDocument {
  const fileName = opts.fileName;
  const language = opts.language;
  const contentType = opts.contentType;
  if (typeof input !== 'string') {
    if (input && typeof input === 'object') {
      checkObjectInputSize(input);
      return parseJsonValue(input, { fileName, language });
    }
    throw new TranscriptDocumentError('unsupported-json', 'Unsupported transcript content');
  }
  if (byteLength(input) > TRANSCRIPT_DOCUMENT_LIMITS.maxBytes) {
    throw new TranscriptDocumentError('too-large', 'Transcript content exceeds the supported size');
  }
  if (!input.replace(/^\uFEFF/, '').trim()) {
    throw new TranscriptDocumentError('empty-content', 'Transcript content is empty');
  }
  const jsonDeclared = isJsonDeclared(fileName, contentType);
  const textDeclared = isTextDeclared(fileName, contentType);
  // Declared JSON never falls through to plain text: invalid JSON rejects.
  if (jsonDeclared) {
    const trimmedDeclared = input.replace(/^\uFEFF/, '').trim();
    if (!/^[\[{]/.test(trimmedDeclared)) {
      throw new TranscriptDocumentError('unsupported-json', 'Declared JSON transcript must be JSON content');
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmedDeclared);
    } catch {
      throw new TranscriptDocumentError('unsupported-json', 'Transcript JSON is malformed');
    }
    return parseJsonValue(parsed, { fileName, language });
  }
  // Declared plain text stays plain even if it begins with braces/arrows.
  // Exception: JSON content-type already handled above, so a known explicit
  // JSON draft envelope parses as JSON only when the caller passes JSON
  // metadata (contentType application/json or .json file name).
  if (textDeclared) {
    const text = input.replace(/\r\n/g, '\n').replace(/^\uFEFF/, '');
    return { kind: 'text', format: 'txt', text, language, fileName };
  }
  const trimmed = input.replace(/^\uFEFF/, '').trim();
  // JSON first: provider segments, draft envelopes, or OCR text-only objects.
  if (/^[\[{]/.test(trimmed)) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      throw new TranscriptDocumentError('unsupported-json', 'Transcript JSON is malformed');
    }
    return parseJsonValue(parsed, { fileName, language });
  }
  const format = detectTranscriptFormat(input, fileName);
  if (format === 'vtt' || /^(?:\uFEFF)?WEBVTT(\s|$)/.test(input.replace(/^\uFEFF/, '').trim())) {
    return parseVttDocument(input, { fileName, language });
  }
  if (format === 'srt') {
    return parseSrtDocument(input, { fileName, language });
  }
  // Plain text is untimed by contract; whitespace and newlines are preserved.
  const text = input.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return { kind: 'text', format: 'txt', text, language, fileName };
}

// Serialize a timed document back to canonical JSON bytes. Raw canonical bytes
// are preserved independently by callers; this is the parsed editing view.
// Language, fileName, and settings round-trip so draft editing does not strip
// unrelated envelope metadata (the UI decodes envelope settings separately).
export function timedDocumentToJson(doc: TimedTranscriptDocument): string {
  return JSON.stringify({
    kind: 'timed',
    format: doc.format,
    ...(doc.language !== undefined ? { language: doc.language } : {}),
    ...(doc.fileName !== undefined ? { fileName: doc.fileName } : {}),
    ...(doc.settings !== undefined ? { settings: doc.settings } : {}),
    segments: doc.cues.map(cue => ({
      id: cue.id,
      start: cue.start,
      end: cue.end,
      text: cue.text,
      ...(cue.speaker ? { speaker: cue.speaker } : {}),
      ...(cue.words ? { words: cue.words } : {}),
    })),
  });
}

export function textDocumentToJson(doc: TextTranscriptDocument): string {
  return JSON.stringify({
    kind: 'text',
    format: doc.format,
    ...(doc.language !== undefined ? { language: doc.language } : {}),
    ...(doc.fileName !== undefined ? { fileName: doc.fileName } : {}),
    ...(doc.settings !== undefined ? { settings: doc.settings } : {}),
    text: doc.text,
  });
}

export function documentToJson(doc: TranscriptDocument): string {
  return doc.kind === 'timed' ? timedDocumentToJson(doc) : textDocumentToJson(doc);
}

// Convert timed cues to the legacy SrtEntry shape used by the editor store.
export function cuesToSrtEntries(cues: TranscriptCue[]): Array<{
  id: string; startTime: number; endTime: number; text: string; lines: number; words?: TranscriptWord[]; speaker?: string;
}> {
  return cues.map(cue => ({
    id: cue.id,
    startTime: cue.start,
    endTime: cue.end,
    text: cue.text,
    lines: cue.text.split('\n').length,
    ...(cue.words ? { words: cue.words } : {}),
    ...(cue.speaker ? { speaker: cue.speaker } : {}),
  }));
}

export function isTranscriptDocumentError(error: unknown): error is TranscriptDocumentError {
  return error instanceof TranscriptDocumentError;
}
